/**
 * Start time, duration and arrival window inputs for the job create and
 * detail pages (tracker item 40). The duration starts at the job's current
 * value (or empty = "from the lines" on create) and is only sent when the
 * user edits it, so saving other fields never pins an AUTO job; "Reset to
 * suggested" sends durationSource AUTO.
 */

import React from 'react';
import * as types from '../api/types';
import { inputClass, labelClass } from './formStyles';
import { ARRIVAL_WINDOW_OPTIONS, formatDuration, suggestionHint } from '../pages/dispatchUi';

export interface TimeFieldValues {
  startTime: string;
  duration: string;
  durationTouched: boolean;
  resetDuration: boolean;
  window: string;
}

export function timeFieldsFromJob(job?: types.Job | null): TimeFieldValues {
  const current = job ? job.estimatedDurationMinutes ?? job.suggestedDurationMinutes : undefined;
  return {
    startTime: job?.scheduledStartTime ?? '',
    duration: current ? String(current) : '',
    durationTouched: false,
    resetDuration: false,
    window: job?.arrivalWindowMinutes ? String(job.arrivalWindowMinutes) : '',
  };
}

/**
 * The request fields for the time inputs: only what changed (always all
 * given values when there is no job yet). Returns an error message instead
 * when the values are invalid.
 */
export function timeFieldsPatch(values: TimeFieldValues, job: types.Job | null, scheduledDate: string): types.UpdateJobRequest | string {
  const patch: types.UpdateJobRequest = {};
  if (values.startTime && !scheduledDate) return 'A start time needs a scheduled date.';
  if (values.startTime !== (job?.scheduledStartTime ?? '')) patch.scheduledStartTime = values.startTime || null;
  if (values.resetDuration) {
    patch.durationSource = 'AUTO';
  } else if (values.durationTouched && values.duration !== '') {
    const minutes = Number(values.duration);
    if (!Number.isInteger(minutes) || minutes < 5 || minutes > 1440) return 'Duration must be a whole number of minutes from 5 to 1440.';
    const current = job ? job.estimatedDurationMinutes ?? job.suggestedDurationMinutes : undefined;
    if (!job || minutes !== current || job.durationSource === 'MANUAL') patch.estimatedDurationMinutes = minutes;
  }
  const window = values.window ? Number(values.window) : null;
  if (window !== (job?.arrivalWindowMinutes ?? null)) patch.arrivalWindowMinutes = window;
  return patch;
}

export function timeFieldsChanged(values: TimeFieldValues, job: types.Job | null): boolean {
  return values.startTime !== (job?.scheduledStartTime ?? '')
    || values.resetDuration
    || values.durationTouched
    || values.window !== (job?.arrivalWindowMinutes ? String(job.arrivalWindowMinutes) : '');
}

interface JobTimeFieldsProps {
  idPrefix: string;
  values: TimeFieldValues;
  onChange: (values: TimeFieldValues) => void;
  disabled?: boolean;
  /** No start time without a date */
  hasDate: boolean;
  /** The job (detail page); omitted on create */
  job?: types.Job | null;
}

export function JobTimeFields({ idPrefix, values, onChange, disabled, hasDate, job }: JobTimeFieldsProps) {
  const manual = !values.resetDuration && ((job?.durationSource === 'MANUAL') || values.durationTouched);
  const suggested = job?.suggestedDurationMinutes;
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-start`} className={labelClass}>Start time</label>
        <input id={`${idPrefix}-start`} type="time" step={900} className={inputClass} value={values.startTime} disabled={disabled || !hasDate}
          onChange={(event) => onChange({ ...values, startTime: event.target.value })} />
        <p className="mt-1 text-xs text-gray-500">{hasDate ? 'Empty = no time set (all day).' : 'Set a date first.'}</p>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-duration`} className={labelClass}>
          Duration (minutes)
          {job && manual && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">Manual</span>}
        </label>
        <input id={`${idPrefix}-duration`} type="number" min={5} max={1440} step={5} className={inputClass} value={values.duration} disabled={disabled}
          placeholder={job ? undefined : 'From the work items'}
          onChange={(event) => onChange({ ...values, duration: event.target.value, durationTouched: true, resetDuration: false })} />
        <p className="mt-1 text-xs text-gray-500">
          {job ? suggestionHint(suggested) : 'Leave empty to add up the work items’ typical durations.'}
          {job && manual && suggested && (
            <>
              {' '}
              <button type="button" className="font-semibold text-blue-700 hover:underline" disabled={disabled}
                onClick={() => onChange({ ...values, duration: String(suggested), durationTouched: false, resetDuration: true })}>
                Reset to suggested
              </button>
            </>
          )}
        </p>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-window`} className={labelClass}>Arrival window</label>
        <select id={`${idPrefix}-window`} className={inputClass} value={values.window} disabled={disabled}
          onChange={(event) => onChange({ ...values, window: event.target.value })}>
          <option value="">None</option>
          {ARRIVAL_WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          {values.window && !ARRIVAL_WINDOW_OPTIONS.some((o) => String(o.value) === values.window) && (
            <option value={values.window}>{formatDuration(Number(values.window))}</option>
          )}
        </select>
      </div>
    </>
  );
}
